import React, { FC, useState } from 'react';
import { Box } from '@mui/material';

import { Typography } from '../Typography/Typography';
import Accordion from '../Accordion/Accordion';

import { accordionStyle } from './styles';

interface NutritionalValue {
  url: string;
  ingredientName: string;
}

interface NutritionalSectionProps {
  isMealKit: boolean;
  // For dishes - simple single URL
  nutritionalValuesUrl?: string;
  // For meal kits - array of nutritional values
  nutritionalValues?: NutritionalValue[];
}

export const NutritionalSection: FC<NutritionalSectionProps> = ({
  isMealKit,
  nutritionalValuesUrl,
  nutritionalValues = [],
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const renderNutritionalImage = (
    url: string,
    ingredientName?: string,
    showHeader = false
  ) => (
    <React.Fragment key={url}>
      {ingredientName && showHeader && (
        <Typography
          variant="body1"
          component="span"
          color="text.secondary"
          i18nKey="meals:dishDetails.withHelper"
          i18nComponents={{
            ingredientName: (
              <Typography
                variant="body1"
                component="span"
                color="text.secondary"
                i18nKey="common:value"
                i18nParams={{ value: ingredientName?.toLowerCase() }}
              >
                {ingredientName?.toLowerCase()}
              </Typography>
            ),
          }}
        >
          {`With ${ingredientName?.toLowerCase()}`}
        </Typography>
      )}
      <img
        src={url}
        alt={`Nutritional values${
          ingredientName ? ` for ${ingredientName}` : ''
        }`}
        height={162}
        style={{
          objectFit: 'contain',
          alignSelf: 'stretch',
        }}
      />
    </React.Fragment>
  );

  return (
    <Accordion
      title="Nutritional values"
      expanded={isExpanded}
      onChange={() => setIsExpanded(!isExpanded)}
      accordionStyle={accordionStyle}
      i18nKeyTitle="meals:dishDetails.nutritionalValues"
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        gap="16px"
        alignSelf="stretch"
      >
        {!isMealKit && nutritionalValuesUrl ? (
          // Simple dish nutritional values
          renderNutritionalImage(nutritionalValuesUrl)
        ) : isMealKit && nutritionalValues.length > 0 ? (
          // Meal kit nutritional values (multiple)
          nutritionalValues.map((nutritional) =>
            renderNutritionalImage(
              nutritional.url,
              nutritional.ingredientName,
              nutritionalValues.length > 1
            )
          )
        ) : (
          <Typography variant="body2" color="text.secondary">
            {'No nutritional information available'}
          </Typography>
        )}
      </Box>
    </Accordion>
  );
};

export default NutritionalSection;
